// src/editor-overlay.js
// Editor overlay for visual element selection

import { getParentOrigin } from './parent-origin.js';

let currentHighlight = null;
let overlayActive = false;
let overlayElement = null;
let isPreviewMode = false;

/**
 * Check if editor mode is currently active
 * Used by tracker to prevent events from firing during editing
 * @returns {boolean} True if editor overlay is active
 */
export function isEditorActive() {
  return overlayActive;
}

/**
 * Check if preview mode is currently active
 * Used by tracker to send event logs to parent
 * @returns {boolean} True if preview mode is active
 */
export function isPreviewActive() {
  return isPreviewMode;
}

/**
 * Get allowed parent origins based on environment
 */
function getAllowedOrigins() {
  return [
    'http://localhost',
    'https://eventfoundry.app'
  ];
}

/**
 * Validate that postMessage came from allowed origin
 * @param {string} origin - The origin from the message event
 * @returns {boolean} True if origin is allowed
 */
function isOriginAllowed(origin) {
  const allowed = getAllowedOrigins();

  // Check for exact match or localhost with any port
  return allowed.some(allowedOrigin => {
    if (allowedOrigin === 'http://localhost') {
      return origin.startsWith('http://localhost');
    }
    return origin === allowedOrigin;
  });
}

/**
 * Create and activate editor overlay
 */
export function createEditorOverlay() {
  if (overlayActive) {
    return;
  }

  overlayActive = true;

  // Add overlay styles
  const style = document.createElement('style');
  style.textContent = `
    .eventfoundry-highlight {
      outline: 2px solid #3498db !important;
      outline-offset: 2px;
      cursor: pointer !important;
    }
    .eventfoundry-test-highlight {
      outline: 3px solid #e74c3c !important;
      outline-offset: 3px;
      background-color: rgba(231, 76, 60, 0.1) !important;
    }
    .eventfoundry-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 999998;
      pointer-events: none;
      cursor: crosshair;
    }
    body.eventfoundry-edit-mode,
    body.eventfoundry-edit-mode * {
      cursor: crosshair !important;
    }
  `;
  document.head.appendChild(style);

  // Create overlay div (for visual indication - crosshair cursor)
  const overlay = document.createElement('div');
  overlay.className = 'eventfoundry-overlay';
  document.body.appendChild(overlay);

  // Store overlay element reference for show/hide
  overlayElement = overlay;

  // Add edit mode class to body for crosshair cursor
  document.body.classList.add('eventfoundry-edit-mode');

  // Mouseover - highlight element
  // Use document.body with capture phase to intercept all mouseover events
  document.body.addEventListener('mouseover', (e) => {
    // Skip highlighting in Preview Mode
    if (isPreviewMode) {
      return;
    }

    // Ignore the overlay itself and EventFoundry-specific elements
    if (e.target.classList.contains('eventfoundry-overlay')) return;

    // Remove previous highlight
    if (currentHighlight) {
      currentHighlight.classList.remove('eventfoundry-highlight');
    }

    // Add highlight to hovered element
    e.target.classList.add('eventfoundry-highlight');
    currentHighlight = e.target;
  }, true);

  // Mouseout - remove highlight
  document.body.addEventListener('mouseout', (e) => {
    // Skip in Preview Mode
    if (isPreviewMode) {
      return;
    }

    // Ignore the overlay itself
    if (e.target.classList.contains('eventfoundry-overlay')) return;
    e.target.classList.remove('eventfoundry-highlight');
  }, true);

  // Click - select element
  document.body.addEventListener('click', (e) => {
    // Ignore the overlay itself
    if (e.target.classList.contains('eventfoundry-overlay')) {
      return;
    }

    // If in Preview Mode, let click pass through normally
    if (isPreviewMode) {
      return; // Don't prevent default, don't select element
    }

    // Edit Mode: Intercept click for element selection
    e.preventDefault();
    e.stopPropagation();

    // Extract element data
    const element = e.target;

    // Filter out EventFoundry classes from className
    let cleanClassName = '';
    if (element.className && typeof element.className === 'string') {
      cleanClassName = element.className
        .trim()
        .split(/\s+/)
        .filter(cls => !cls.startsWith('eventfoundry-'))
        .join(' ');
    }

    // Extract page path (everything after domain)
    const pagePath = window.location.pathname + window.location.search + window.location.hash;

    const elementData = {
      tagName: element.tagName.toLowerCase(),
      text: element.textContent.trim().substring(0, 50),
      id: element.id,
      className: cleanClassName,
      selector: computeSelector(element),
      page_path: pagePath
    };

    // Send to parent window (Vue app) using the verified parent origin
    const targetOrigin = getParentOrigin();
    if (targetOrigin) {
      window.parent.postMessage({
        type: 'ELEMENT_SELECTED',
        element: elementData
      }, targetOrigin);
      console.log('→ ELEMENT_SELECTED sent to parent');
    } else {
      console.warn('EventFoundry: Cannot send ELEMENT_SELECTED - parent origin not established');
    }
  }, true);

  // Listen for "test selector" and "count matches" messages from parent
  window.addEventListener('message', (event) => {
    // Validate origin
    if (!isOriginAllowed(event.origin)) {
      return; // Silently ignore (already logged in editor-mode.js)
    }

    // Handle HIGHLIGHT_SELECTOR message
    if (event.data.type === 'HIGHLIGHT_SELECTOR') {
      console.log('← HIGHLIGHT_SELECTOR received:', event.data.selector);
      highlightMatchingElements(event.data.selector);
    }

    // Handle COUNT_MATCHES message
    if (event.data.type === 'COUNT_MATCHES') {
      const selector = event.data.selector;
      let count = 0;

      try {
        count = document.querySelectorAll(selector).length;
      } catch (e) {
        // Invalid selector, count stays 0
        count = 0;
      }

      // Send count back to parent using the verified parent origin
      const targetOrigin = getParentOrigin();
      if (targetOrigin) {
        window.parent.postMessage({
          type: 'MATCH_COUNT_RESULT',
          selector: selector,
          count: count
        }, targetOrigin);
      }
    }
  });
}

/**
 * Escape special characters in CSS selectors
 * Handles: . : [ ] # ( ) , > + ~ @ ! $ % ^ & * = | \ / ' "
 */
function escapeSelector(str) {
  if (!str) return '';
  // Escape special CSS characters
  return str.replace(/([!"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~])/g, '\\$1');
}

/**
 * Check if an ID looks dynamically generated (UUID, timestamp, random hash)
 * Dynamic IDs are unreliable for tracking as they change on page reloads
 */
function isDynamicId(id) {
  if (!id) return false;

  // Check for UUID patterns (e.g., "abc-123-def-456")
  const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
  if (uuidPattern.test(id)) return true;

  // Check for timestamp-like patterns (13 digit numbers)
  const timestampPattern = /\d{13}/;
  if (timestampPattern.test(id)) return true;

  // Check for random hash patterns (long hex strings)
  const hashPattern = /^[a-f0-9]{16,}$/i;
  if (hashPattern.test(id)) return true;

  // Check for IDs that end with random numbers (e.g., "button-12345")
  const randomSuffixPattern = /-\d{4,}$/;
  if (randomSuffixPattern.test(id)) return true;

  return false;
}

/**
 * Validate that a selector uniquely identifies the target element
 * @param {Element} element - The target element
 * @param {string} selector - The CSS selector to validate
 * @returns {boolean} True if selector returns exactly 1 element and it matches the target
 */
function validateSelectorUniqueness(element, selector) {
  try {
    const matches = document.querySelectorAll(selector);
    // Must match exactly 1 element, and it must be our target element
    return matches.length === 1 && matches[0] === element;
  } catch (e) {
    // Invalid selector
    return false;
  }
}

/**
 * Get element's classes, filtering out EventFoundry internal classes
 * @param {Element} element - The target element
 * @returns {string[]} Array of clean class names
 */
function getCleanClasses(element) {
  if (!element.className || typeof element.className !== 'string') {
    return [];
  }

  return element.className
    .trim()
    .split(/\s+/)
    .filter(cls => cls && !cls.startsWith('eventfoundry-'));
}

/**
 * Get a selector for the element's parent
 * @param {Element} element - The child element
 * @param {boolean} useFirstClassOnly - If true, use only first class; otherwise use tag name
 * @returns {string|null} Parent selector or null if no parent
 */
function getParentSelector(element, useFirstClassOnly = true) {
  const parent = element.parentElement;
  if (!parent) {
    return null;
  }

  const parentClasses = getCleanClasses(parent);

  if (useFirstClassOnly && parentClasses.length > 0) {
    return `.${escapeSelector(parentClasses[0])}`;
  }

  return parent.tagName.toLowerCase();
}

/**
 * Try to add a selector to the array if it uniquely identifies the element
 * @param {Array} selectors - Array to add selector to
 * @param {string} selector - The CSS selector to try
 * @param {number} priority - Priority score for this selector
 * @param {Element} element - The target element
 * @returns {boolean} True if selector was added (was unique)
 */
function tryAddSelector(selectors, selector, priority, element) {
  if (validateSelectorUniqueness(element, selector)) {
    selectors.push({ selector, priority });
    return true;
  }
  return false;
}

/**
 * Generate ID-based selectors
 * Priority: 100 (highest - IDs should be unique)
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 * @example Returns: [{ selector: '#submit-btn', priority: 100 }]
 */
function generateIdSelectors(element) {
  const selectors = [];

  // Only use non-dynamic IDs
  if (element.id && !isDynamicId(element.id)) {
    const idSelector = `#${escapeSelector(element.id)}`;
    tryAddSelector(selectors, idSelector, 100, element);
  }

  return selectors;
}

/**
 * Generate data attribute selectors
 * Priority: 90 (high - data attributes often used for tracking)
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 * @example Returns: [{ selector: '[data-track="signup"]', priority: 90 }]
 */
function generateDataAttrSelectors(element) {
  const selectors = [];

  const dataAttrs = Array.from(element.attributes)
    .filter(attr => attr.name.startsWith('data-') && attr.value);

  // Try each data attribute individually
  for (const attr of dataAttrs) {
    const attrSelector = `[${attr.name}="${escapeSelector(attr.value)}"]`;
    if (tryAddSelector(selectors, attrSelector, 90, element)) {
      break; // Use first unique data attribute found
    }
  }

  return selectors;
}

/**
 * Generate class-based selectors with improved specificity
 * Tries multiple strategies:
 * 1. Tag + single class (Priority: 85) - e.g., button.btn-primary
 * 2. Multi-class combinations (Priority: 82) - e.g., .btn.btn-primary
 * 3. Single class (Priority: 80) - e.g., .btn-primary
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 * @example Returns: [
 *   { selector: 'button.btn-primary', priority: 85 },
 *   { selector: '.btn.btn-primary', priority: 82 }
 * ]
 */
function generateClassSelectors(element) {
  const selectors = [];
  const classes = getCleanClasses(element);

  if (classes.length === 0) {
    return selectors;
  }

  const tagName = element.tagName.toLowerCase();

  // Strategy 1: Try tag + single class combinations (most specific)
  for (const cls of classes) {
    const tagClassSelector = `${tagName}.${escapeSelector(cls)}`;
    if (tryAddSelector(selectors, tagClassSelector, 85, element)) {
      break; // Found a unique tag+class combo
    }
  }

  // Strategy 2: Try multi-class combinations (2 classes)
  if (classes.length >= 2) {
    // Try combining first two classes
    const multiClassSelector = `.${escapeSelector(classes[0])}.${escapeSelector(classes[1])}`;
    tryAddSelector(selectors, multiClassSelector, 82, element);

    // If element has 3+ classes, try first three
    if (classes.length >= 3) {
      const tripleClassSelector = `.${escapeSelector(classes[0])}.${escapeSelector(classes[1])}.${escapeSelector(classes[2])}`;
      tryAddSelector(selectors, tripleClassSelector, 82, element);
    }
  }

  // Strategy 3: Try single classes (original behavior - fallback)
  for (const cls of classes) {
    const classSelector = `.${escapeSelector(cls)}`;
    if (tryAddSelector(selectors, classSelector, 80, element)) {
      break; // Use first unique class found
    }
  }

  return selectors;
}

/**
 * Generate semantic attribute selectors
 * Priority: 70 (medium - attributes like type, name, href can be meaningful)
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 * @example Returns: [{ selector: 'button[type="submit"]', priority: 70 }]
 */
function generateAttributeSelectors(element) {
  const selectors = [];
  const tagName = element.tagName.toLowerCase();

  // Semantic attributes to check (in priority order)
  const semanticAttrs = ['type', 'name', 'href', 'role', 'aria-label'];

  for (const attrName of semanticAttrs) {
    const attrValue = element.getAttribute(attrName);
    if (attrValue) {
      // Try tag + attribute combination (more specific)
      const tagAttrSelector = `${tagName}[${attrName}="${escapeSelector(attrValue)}"]`;
      if (tryAddSelector(selectors, tagAttrSelector, 70, element)) {
        break; // Found unique semantic selector
      }
    }
  }

  return selectors;
}

/**
 * Generate selectors with parent context for improved specificity
 * Priority: 65-75 (medium-high - adds context to prevent collisions)
 *
 * Strategies:
 * 1. Parent class + child class (Priority: 75)
 * 2. Parent class + child tag+class (Priority: 72)
 * 3. Parent class + child tag (Priority: 65)
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 * @example Returns: [{ selector: '.modal .btn-primary', priority: 75 }]
 */
function generateParentContextSelectors(element) {
  const selectors = [];
  const parentSelector = getParentSelector(element, true);

  if (!parentSelector) {
    return selectors; // No parent
  }

  const tagName = element.tagName.toLowerCase();
  const classes = getCleanClasses(element);

  // Strategy 1: Parent + child's first class
  if (classes.length > 0) {
    const parentChildClassSelector = `${parentSelector} .${escapeSelector(classes[0])}`;
    tryAddSelector(selectors, parentChildClassSelector, 75, element);
  }

  // Strategy 2: Parent + child tag+class
  if (classes.length > 0) {
    const parentTagClassSelector = `${parentSelector} ${tagName}.${escapeSelector(classes[0])}`;
    tryAddSelector(selectors, parentTagClassSelector, 72, element);
  }

  // Strategy 3: Parent + child tag (basic context)
  const parentTagSelector = `${parentSelector} ${tagName}`;
  tryAddSelector(selectors, parentTagSelector, 65, element);

  return selectors;
}

/**
 * Generate structural selector using nth-child
 * Priority: 50 (lowest - fragile, breaks when DOM order changes)
 *
 * This is the ultimate fallback when semantic selectors don't work.
 * Uses parent context to make it slightly more resilient.
 *
 * @param {Element} element - The target element
 * @returns {Array} Array of selector candidates with priority scores
 * @example Returns: [{ selector: '.container button:nth-child(3)', priority: 50 }]
 */
function generateStructuralSelector(element) {
  const selectors = [];
  const tagName = element.tagName.toLowerCase();
  const parent = element.parentElement;

  if (!parent) {
    // No parent - just return tag name
    return [{ selector: tagName, priority: 10 }];
  }

  // Build nth-child selector with parent context
  const parentSelector = getParentSelector(element, true);
  const siblings = Array.from(parent.children);
  const index = siblings.indexOf(element) + 1;
  const nthChildSelector = `${parentSelector} ${tagName}:nth-child(${index})`;

  tryAddSelector(selectors, nthChildSelector, 50, element);

  return selectors;
}

/**
 * Compute CSS selector for element using priority scoring system
 *
 * Orchestrates multiple selector generation strategies and returns
 * the highest priority selector that uniquely identifies the element.
 *
 * Priority scoring (highest to lowest):
 * - ID (non-dynamic): 100
 * - Data attributes: 90
 * - Tag + class: 85
 * - Multi-class: 82
 * - Single class: 80
 * - Parent + child class: 75
 * - Parent + tag+class: 72
 * - Semantic attributes: 70
 * - Parent + tag: 65
 * - Structural (nth-child): 50
 *
 * @param {Element} element - The target element
 * @returns {string} The best CSS selector for the element
 */
function computeSelector(element) {
  const selectors = [];

  // Collect selector candidates from all strategies
  selectors.push(...generateIdSelectors(element));
  selectors.push(...generateDataAttrSelectors(element));
  selectors.push(...generateClassSelectors(element));
  selectors.push(...generateAttributeSelectors(element));
  selectors.push(...generateParentContextSelectors(element));
  selectors.push(...generateStructuralSelector(element));

  // Sort by priority (highest first) and return the best selector
  if (selectors.length > 0) {
    selectors.sort((a, b) => b.priority - a.priority);
    return selectors[0].selector;
  }

  // Ultimate fallback: just use tag name (will match multiple elements, but won't crash)
  return element.tagName.toLowerCase();
}

/**
 * Highlight all elements matching selector (for testing)
 */
function highlightMatchingElements(selector) {
  // Remove previous test highlights
  document.querySelectorAll('.eventfoundry-test-highlight').forEach(el => {
    el.classList.remove('eventfoundry-test-highlight');
  });

  // Add highlights to matching elements
  try {
    const elements = document.querySelectorAll(selector);
    elements.forEach(el => {
      el.classList.add('eventfoundry-test-highlight');
    });

    // Auto-remove after 3 seconds
    setTimeout(() => {
      elements.forEach(el => {
        el.classList.remove('eventfoundry-test-highlight');
      });
    }, 3000);
  } catch (e) {
    console.error('EventFoundry: Invalid selector', selector, e);
  }
}

/**
 * Enable Preview Mode - hide overlay and allow normal interactions
 */
export function enablePreviewOverlay() {
  if (!overlayActive) {
    console.warn('EventFoundry: Cannot enable preview - overlay not active');
    return;
  }

  isPreviewMode = true;

  console.log('EventFoundry: Preview Mode enabled - all interactions allowed');

  // Hide overlay visually
  if (overlayElement) {
    overlayElement.style.display = 'none';
  }

  // Remove edit mode class to restore normal cursors
  document.body.classList.remove('eventfoundry-edit-mode');

  // Remove current highlight if any
  if (currentHighlight) {
    currentHighlight.classList.remove('eventfoundry-highlight');
    currentHighlight = null;
  }
}

/**
 * Disable Preview Mode - show overlay and enable element selection
 */
export function disablePreviewOverlay() {
  if (!overlayActive) {
    console.warn('EventFoundry: Cannot disable preview - overlay not active');
    return;
  }

  isPreviewMode = false;

  console.log('EventFoundry: Preview Mode disabled - Edit Mode active');

  // Show overlay again
  if (overlayElement) {
    overlayElement.style.display = '';
  }

  // Restore edit mode class for crosshair cursor
  document.body.classList.add('eventfoundry-edit-mode');
}
